import React from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useTranslation, useConfig } from '@askporter/config-provider';
import useSetLanguage from '../../hooks/useSetLanguage';

/**
 * Language picker component which uses tenant config to display a list of available languages and updates the i18n
 * language based on user selection
 *
 * @returns LanguagePicker component
 */
const LanguagePicker: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { tenantLanguages } = useConfig();
  const { setLanguage } = useSetLanguage();

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const languageCode = event.target.value as string;
    setLanguage({ languageCode });
  };

  return (
    <Box>
      {tenantLanguages && tenantLanguages.length > 0 && (
        <FormControl variant="standard">
          {/* Style label to ensure it is accessible, but not visible on screen, refer "Absolutely positioning content": 
        https://webaim.org/techniques/css/invisiblecontent/ */}
          <InputLabel
            id="language-selector-label"
            sx={{
              position: 'absolute',
              left: '-10000px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          >
            {t('ns.global:footer:language_selector')}
          </InputLabel>
          <Box display="flex" alignItems="center" color="primary.contrastText">
            <LanguageIcon />
            <Select
              data-testid="language-selector"
              labelId="language-selector-label"
              value={i18n.language}
              onChange={handleLanguageChange}
              label="Language"
              sx={{ color: 'inherit', ml: 2 }}
            >
              {/* We can use the client config to provide a list of language choices */}
              {tenantLanguages.sort().map((language, idx) => {
                return (
                  <MenuItem key={idx} value={language.languageCode}>
                    {language.languageName}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        </FormControl>
      )}
    </Box>
  );
};

export default LanguagePicker;
